export type ReturnToTarget = {
  type: 'home' | 'automations'
  url: string
}

const RETURN_TO_TARGET_STORAGE_KEY = 'jrReturnToTarget'

const isNeoletterHostname = (hostname: string): boolean =>
  hostname === 'neoletter.com' || hostname.endsWith('.neoletter.com')

const isJustRelateHostname = (hostname: string): boolean =>
  hostname === 'justrelate.com' || hostname.endsWith('.justrelate.com')

const getValidUrl = (
  value: string | null,
  isValidHostname: (hostname: string) => boolean,
): string | null => {
  if (!value) return null

  try {
    const parsed = new URL(value)
    if (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      isValidHostname(parsed.hostname)
    ) {
      return value
    }
  } catch {
    // not a valid URL
  }

  return null
}

const getStorage = (): Storage | null => {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

const isValidReturnToTarget = (value: unknown): value is ReturnToTarget => {
  if (!value || typeof value !== 'object') return false

  const returnTo = value as Partial<ReturnToTarget>
  if (returnTo.type === 'home') {
    return (
      getValidUrl(returnTo.url ?? null, isJustRelateHostname) === returnTo.url
    )
  }

  if (returnTo.type === 'automations') {
    return (
      getValidUrl(returnTo.url ?? null, isNeoletterHostname) === returnTo.url
    )
  }

  return false
}

const loadStoredReturnToTarget = (): ReturnToTarget | null => {
  const storage = getStorage()
  if (!storage) return null

  try {
    const storedValue = storage.getItem(RETURN_TO_TARGET_STORAGE_KEY)
    if (!storedValue) return null

    const parsedValue = JSON.parse(storedValue)
    if (isValidReturnToTarget(parsedValue)) return parsedValue

    storage.removeItem(RETURN_TO_TARGET_STORAGE_KEY)
  } catch {
    storage.removeItem(RETURN_TO_TARGET_STORAGE_KEY)
  }

  return null
}

const storeReturnToTarget = (returnTo: ReturnToTarget): void => {
  const storage = getStorage()
  if (!storage) return

  try {
    storage.setItem(RETURN_TO_TARGET_STORAGE_KEY, JSON.stringify(returnTo))
  } catch {
    // ignore storage failures
  }
}

const clearStoredReturnToTarget = (): void => {
  const storage = getStorage()
  if (!storage) return

  try {
    storage.removeItem(RETURN_TO_TARGET_STORAGE_KEY)
  } catch {
    // ignore storage failures
  }
}

export const getReturnToTarget = (): ReturnToTarget | null => {
  if (typeof window === 'undefined') return null

  const searchParams = new URLSearchParams(window.location.search)
  const hasReturnToParameter =
    searchParams.has('return_to') || searchParams.has('returnTo')

  const homeUrl = getValidUrl(
    searchParams.get('return_to'),
    isJustRelateHostname,
  )
  if (homeUrl) {
    const returnTo = { type: 'home', url: homeUrl } as const
    storeReturnToTarget(returnTo)
    return returnTo
  }

  const automationsUrl = getValidUrl(
    searchParams.get('returnTo'),
    isNeoletterHostname,
  )
  if (automationsUrl) {
    const returnTo = { type: 'automations', url: automationsUrl } as const
    storeReturnToTarget(returnTo)
    return returnTo
  }

  if (hasReturnToParameter) {
    clearStoredReturnToTarget()
    return null
  }

  return loadStoredReturnToTarget()
}

export const isAutomationsBannerReturnToTarget = (
  returnTo: ReturnToTarget | null,
): boolean => {
  return returnTo?.type === 'automations'
}
